<script setup lang="ts">
import { ref, computed } from 'vue'
import AltchaField from '@/Components/Forms/AltchaField.vue'
import { Button } from '@/Components/ui/button'
import { Alert, AlertDescription } from '@/Components/ui/alert'
import { AlertCircle, CheckCircle2, Eye, EyeOff } from 'lucide-vue-next'

const emit = defineEmits<{
  (e: 'success'): void
  (e: 'switch-to', view: 'signup' | 'forgot-password'): void
}>()

const form = ref({
  altcha: '',
  email: '',
  password: '',
})

const showPassword = ref(false)
const altchaField = ref<InstanceType<typeof AltchaField> | null>(null)
const isSubmitting = ref(false)
const errors = ref<Record<string, string[]>>({})
const generalError = ref('')
const successMessage = ref('')

const hasErrors = computed(() => Object.keys(errors.value).length > 0 || !!generalError.value)

async function submit() {
  isSubmitting.value = true
  errors.value = {}
  generalError.value = ''

  try {
    await window.axios.post('/login', form.value)
    successMessage.value = 'Login successful! Redirecting...'

    setTimeout(() => {
      emit('success')
      // Reload to get fresh auth state
      window.location.reload()
    }, 500)
  } catch (error: any) {
    if (error.response?.data?.errors) {
      errors.value = error.response.data.errors
    } else if (error.response?.data?.message) {
      generalError.value = error.response.data.message
    } else {
      generalError.value = 'An error occurred. Please try again.'
    }
  } finally {
    isSubmitting.value = false
    altchaField.value?.reset()
  }
}
</script>

<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="text-center">
      <h2 class="text-2xl font-bold text-gray-900 mb-2">Welcome back</h2>
      <p class="text-gray-600">Sign in to your account to continue</p>
    </div>

    <form @submit.prevent="submit" class="space-y-5">
      <!-- Error Alert -->
      <Alert v-if="hasErrors" variant="destructive">
        <AlertCircle class="h-4 w-4" />
        <AlertDescription>
          {{ generalError || 'Please check your credentials and try again.' }}
        </AlertDescription>
      </Alert>

      <!-- Success Alert -->
      <Alert v-if="successMessage" class="border-green-500 text-green-700 [&>svg]:text-green-700">
        <CheckCircle2 class="h-4 w-4" />
        <AlertDescription>{{ successMessage }}</AlertDescription>
      </Alert>

      <!-- Email Field -->
      <div>
        <label for="login-email" class="block text-sm font-semibold text-gray-700 mb-2">
          Email <span class="text-red-500">*</span>
        </label>
        <input
          id="login-email"
          type="email"
          placeholder="m@example.com"
          v-model="form.email"
          required
          class="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-gray-900"
          :class="errors.email ? 'border-red-500' : 'border-gray-200'"
        />
        <p v-if="errors.email" class="text-sm text-red-500 mt-1">{{ errors.email[0] }}</p>
      </div>

      <!-- Password Field -->
      <div>
        <div class="flex items-center justify-between mb-2">
          <label for="login-password" class="block text-sm font-semibold text-gray-700">
            Password <span class="text-red-500">*</span>
          </label>
          <button
            type="button"
            @click="$emit('switch-to', 'forgot-password')"
            class="text-sm font-medium text-blue-600 hover:text-blue-500"
          >
            Forgot password?
          </button>
        </div>
        <div class="relative">
          <input
            id="login-password"
            :type="showPassword ? 'text' : 'password'"
            v-model="form.password"
            required
            class="w-full px-4 py-3 pr-12 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-gray-900"
            :class="errors.password ? 'border-red-500' : 'border-gray-200'"
          />
          <button
            type="button"
            @click="showPassword = !showPassword"
            class="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <EyeOff v-if="showPassword" class="w-5 h-5" />
            <Eye v-else class="w-5 h-5" />
          </button>
        </div>
        <p v-if="errors.password" class="text-sm text-red-500 mt-1">{{ errors.password[0] }}</p>
      </div>

      <div>
        <AltchaField ref="altchaField" v-model="form.altcha" />
        <p v-if="errors.altcha" class="text-sm text-red-500 mt-1">{{ errors.altcha[0] }}</p>
      </div>

      <Button
        type="submit"
        class="w-full bg-blue-600 hover:bg-blue-700 h-auto px-5 py-3.5 text-base font-semibold rounded-xl"
        :disabled="isSubmitting || !form.altcha"
      >
        {{ isSubmitting ? 'Signing in...' : 'Sign In' }}
      </Button>
    </form>

    <!-- Footer -->
    <div class="text-center text-sm text-gray-600">
      Don't have an account?
      <button
        type="button"
        @click="$emit('switch-to', 'signup')"
        class="font-semibold text-blue-600 hover:text-blue-500"
      >
        Sign up
      </button>
    </div>
  </div>
</template>
